// §K — the two-way lifecycle on today's primary job (handoff §4.1 order,
// every block gated on crew_settings): Accept → message thread + Reply →
// GPS check-in (one-shot) → server-persisted checklist + progress →
// Mark complete (disabled until 100%) → the post-complete payment block.
// The v1 localStorage checklist migrates up once (server empty + local has
// checks → write them through the RPC, then clear the key).

import { useState, useEffect, useRef } from 'react';
import { CheckCircle, MapPin, Send } from 'lucide-react';
import { portalColors, portalRadius } from './portalStyles';
import { useCrewLifecycle } from './useCrewLifecycle';
import type { CrewPortalJob, CrewPortalSettings, ChecklistItem } from '../../types/crewPortal';

interface Props {
  job: CrewPortalJob;
  slug: string;
  settings: CrewPortalSettings;
  accentColor: string;
  firstName: string;
  businessName: string;
  businessLogoUrl: string | null;
  onCompleted: () => void;
}

const LEGACY_KEY = 'crewbit_portal_checks';

export function LifecycleSection({ job, slug, settings, accentColor, firstName, businessName, businessLogoUrl, onCompleted }: Props) {
  const { state, messages, busy, error, accept, arrive, setChecklist, complete, requestPay, reply } =
    useCrewLifecycle(slug, job.id, { state: job.state ?? null, messages: job.messages ?? [] });
  const [completed, setCompleted] = useState(job.status === 'completed');
  const [replyDraft, setReplyDraft] = useState('');
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const migratedRef = useRef(false);

  const checklist: ChecklistItem[] = job.checklist ?? [];
  const checks: Record<string, boolean> = state?.checklist_state ?? {};
  const checkedCount = checklist.reduce((acc, _it, i) => acc + (checks[String(i)] ? 1 : 0), 0);
  const allDone = checklist.length === 0 || checkedCount === checklist.length;
  const payLabel = state?.pay_cents != null ? `$${(state.pay_cents / 100).toFixed(state.pay_cents % 100 === 0 ? 0 : 2)}` : null;

  // one-time v1 localStorage → server migration
  useEffect(() => {
    if (migratedRef.current) return;
    migratedRef.current = true;
    if (Object.keys(checks).length > 0 || checklist.length === 0) return;
    try {
      const raw = localStorage.getItem(LEGACY_KEY);
      if (!raw) return;
      const all = JSON.parse(raw) as Record<string, { items: Record<number, boolean> }>;
      const legacy = all[job.id]?.items;
      if (legacy && Object.keys(legacy).length > 0) {
        const migrated: Record<string, boolean> = {};
        for (const [k, v] of Object.entries(legacy)) {
          if (v && Number(k) < checklist.length) migrated[k] = true;
        }
        if (Object.keys(migrated).length > 0) void setChecklist(migrated);
        delete all[job.id];
        localStorage.setItem(LEGACY_KEY, JSON.stringify(all));
      }
    } catch { /* legacy state unreadable — server is the truth now */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot migration on mount
  }, []);

  const toggle = (i: number) => {
    if (completed) return;
    const next = { ...checks };
    if (next[String(i)]) delete next[String(i)];
    else next[String(i)] = true;
    void setChecklist(next);
  };

  const checkIn = () => {
    setGpsError(null);
    if (!navigator.geolocation) { setGpsError('Location isn’t available on this device.'); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGpsBusy(false); void arrive(pos.coords.latitude, pos.coords.longitude); },
      () => { setGpsBusy(false); setGpsError('Couldn’t get your location — check permissions and try again.'); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  const markComplete = async () => {
    const env = await complete();
    if (env.success) { setCompleted(true); onCompleted(); }
  };

  const sendReply = async () => {
    const body = replyDraft.trim();
    if (!body) return;
    const env = await reply(body);
    if (env.success) setReplyDraft('');
  };

  const greenLine = (text: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
      <CheckCircle size={18} color="#2E7D55" />
      <span style={{ fontSize: 14, color: '#2E7D55', fontWeight: 500 }}>{text}</span>
    </div>
  );
  const bigButton = (label: string, onClick: () => void, opts?: { disabled?: boolean; subtle?: boolean }) => (
    <button
      onClick={onClick}
      disabled={opts?.disabled}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        width: '100%', height: 48, borderRadius: portalRadius.button, border: 'none',
        cursor: opts?.disabled ? 'default' : 'pointer',
        background: opts?.subtle ? `${accentColor}14` : accentColor,
        color: opts?.subtle ? accentColor : '#fff',
        opacity: opts?.disabled ? 0.45 : 1,
        fontSize: 15, fontWeight: 600, marginTop: 16, fontFamily: 'inherit',
      }}
    >{label}</button>
  );

  const property = job.location?.name ?? job.title;

  return (
    <div>
      {/* pay chip rides the time line in JobHeroCard; acceptance is the first action */}
      {settings.require_accept && !completed && (
        state?.accepted_at
          ? greenLine(`You accepted this job`)
          : bigButton(busy === 'accept' ? 'Accepting…' : 'Accept this job', () => void accept(), { disabled: busy !== null })
      )}

      {/* message thread */}
      {(messages.length > 0 || !completed) && (
        <div style={{ marginTop: 16 }}>
          {messages.map((m) => {
            const personal = m.author === 'owner' && m.from_voice === 'personal';
            const fromOwner = m.author === 'owner';
            return (
              <div key={m.id} style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'flex-start' }}>
                {fromOwner ? (
                  personal ? (
                    <div style={{ width: 28, height: 28, borderRadius: 999, flexShrink: 0, background: accentColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                      {businessName.charAt(0)}
                    </div>
                  ) : businessLogoUrl ? (
                    <img src={businessLogoUrl} alt="" style={{ width: 28, height: 28, borderRadius: 6, flexShrink: 0, objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: 28, height: 28, borderRadius: 6, flexShrink: 0, background: portalColors.divider, color: portalColors.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                      {businessName.charAt(0)}
                    </div>
                  )
                ) : (
                  <div style={{ width: 28, height: 28, borderRadius: 999, flexShrink: 0, background: portalColors.divider, color: portalColors.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                    {firstName.charAt(0)}
                  </div>
                )}
                <div style={{
                  flex: 1, borderRadius: 12, padding: '10px 12px',
                  background: personal ? `${accentColor}14` : portalColors.background,
                  border: `1px solid ${personal ? `${accentColor}30` : portalColors.divider}`,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: personal ? accentColor : portalColors.muted, marginBottom: 2 }}>
                    {m.author === 'crew' ? `You replied` : personal ? 'Message' : businessName}
                  </div>
                  <div style={{ fontSize: 14, color: portalColors.primary, lineHeight: '19px' }}>{m.body}</div>
                </div>
              </div>
            );
          })}
          {!completed && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input
                value={replyDraft}
                onChange={(e) => setReplyDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void sendReply(); }}
                placeholder="Reply…"
                style={{
                  flex: 1, height: 40, borderRadius: 10, border: `1.5px solid ${portalColors.divider}`,
                  padding: '0 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none', color: portalColors.primary,
                }}
              />
              <button onClick={() => void sendReply()} disabled={busy === 'reply' || !replyDraft.trim()}
                style={{ width: 40, height: 40, borderRadius: 10, border: 'none', background: accentColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: replyDraft.trim() ? 1 : 0.45 }}>
                <Send size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* GPS check-in (one-shot) */}
      {settings.gps_enabled && !completed && (
        state?.arrived_at
          ? greenLine(`Checked in at ${property}`)
          : (
            <div>
              <button onClick={checkIn} disabled={gpsBusy || busy !== null}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  width: '100%', height: 48, borderRadius: portalRadius.button,
                  border: `1.5px solid ${accentColor}`, background: 'transparent', color: accentColor,
                  fontSize: 15, fontWeight: 600, marginTop: 16, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                <MapPin size={17} />
                {gpsBusy ? 'Getting your location…' : 'I’ve arrived — check in'}
              </button>
              <div style={{ fontSize: 12, color: portalColors.muted, marginTop: 6, textAlign: 'center' }}>
                Shares your location once so {businessName} knows you’re on site.
              </div>
              {gpsError && <div style={{ fontSize: 12.5, color: '#C8493F', marginTop: 6, textAlign: 'center' }}>{gpsError}</div>}
            </div>
          )
      )}

      {/* progress + interactive server checklist */}
      {checklist.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, color: portalColors.secondary, marginBottom: 6 }}>
            {checkedCount} of {checklist.length} complete
          </div>
          <div style={{ height: 4, background: portalColors.divider, borderRadius: 2, marginBottom: 12, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: accentColor, borderRadius: 2, width: `${(checkedCount / checklist.length) * 100}%`, transition: 'width 400ms ease' }} />
          </div>
          {checklist.map((item, i) => {
            const isChecked = !!checks[String(i)];
            return (
              <div key={i}>
                <div onClick={() => toggle(i)}
                  style={{ display: 'flex', gap: 12, padding: '10px 0', cursor: completed ? 'default' : 'pointer', minHeight: 44, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: portalRadius.checkbox, flexShrink: 0, marginTop: 1,
                    border: isChecked ? 'none' : `2px solid ${portalColors.veryMuted}`,
                    background: isChecked ? accentColor : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 150ms',
                  }}>
                    {isChecked && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M3 7L6 10L11 4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, color: portalColors.primary, opacity: isChecked ? 0.5 : 1, textDecoration: isChecked ? 'line-through' : 'none' }}>{item.label}</div>
                    {item.notes && <div style={{ fontSize: 12, color: portalColors.muted, marginTop: 2 }}>{item.notes}</div>}
                  </div>
                </div>
                {i < checklist.length - 1 && <div style={{ height: 0.5, background: portalColors.subtleDivider, marginLeft: 36 }} />}
              </div>
            );
          })}
        </div>
      )}

      {/* mark complete */}
      {!completed && (
        <div>
          {bigButton(busy === 'complete' ? 'Finishing…' : 'Mark this job complete', () => void markComplete(), { disabled: !allDone || busy !== null })}
          {!allDone && (
            <div style={{ fontSize: 12, color: portalColors.muted, marginTop: 6, textAlign: 'center' }}>
              Finish the checklist to mark this done.
            </div>
          )}
        </div>
      )}

      {/* post-complete payment block */}
      {completed && (
        <div style={{ marginTop: 16, borderTop: `1px solid ${portalColors.divider}`, paddingTop: 4 }}>
          {greenLine('Job complete — nice work')}
          {state?.paid_at ? (
            <div>
              {greenLine(`Paid${payLabel ? ` ${payLabel}` : ''} · ${state.paid_method === 'cash' ? 'Cash' : 'e-Transfer'} · ${new Date(state.paid_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`)}
              {state.receipt_ref && (
                <a href={`?receipt=${state.receipt_ref}`}
                  style={{ display: 'block', textAlign: 'center', marginTop: 12, fontSize: 14, fontWeight: 600, color: accentColor, textDecoration: 'none' }}>
                  View receipt
                </a>
              )}
            </div>
          ) : state?.pay_requested_at ? (
            <div style={{ fontSize: 14, color: portalColors.secondary, marginTop: 12, textAlign: 'center' }}>
              Payment requested — waiting on {businessName}.
            </div>
          ) : (
            bigButton(busy === 'requestPay' ? 'Sending…' : `Request payment${payLabel ? ` · ${payLabel}` : ''}`, () => void requestPay(), { disabled: busy !== null, subtle: true })
          )}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 12, fontSize: 13, color: '#C8493F', background: '#C8493F14', border: '1px solid #C8493F30', borderRadius: 10, padding: '9px 12px' }}>
          {error}
        </div>
      )}
    </div>
  );
}
